// frontend/src/components/SaidaMilhasModal/SaidaMilhasModal.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Select, DatePicker, Button, message, InputNumber, Row, Col, Input, Radio, Divider, Space, Checkbox, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getPortadores, getProgramasDoPortador, getPassageiros, registrarSaidaAvulsa, registrarVenda, updateTransacaoMilhas, updateOperacaoVenda } from '../../services/milhasService';
import { getContas, getCategorias } from '../../services/financeService';
import { createTransacao } from '../../services/transactionService';
import PassageiroModal from '../PassageiroModal/PassageiroModal';
import { getErrorMessage } from '../../utils/errorHandler';
import MobileLaunchSheet from '../MobileLaunchSheet/MobileLaunchSheet';
import { useAuth } from '../../contexts/AuthContext';
import ModuloUpsellModal from '../ModuloUpsellModal/ModuloUpsellModal';

const { Option } = Select;
const { Text } = Typography;

const mapEmissaoPayload = (e) => {
    const item = {
        quantidade_milhas_utilizada: e.quantidade_milhas_utilizada,
        data_emissao: e.data_emissao ? e.data_emissao.format('YYYY-MM-DD') : null,
        codigo_localizador: e.codigo_localizador || '',
    };
    if (e.id != null) item.id = e.id;
    if (e.cpf_desconhecido) {
        item.cpf_desconhecido = true;
        item.nome_referencia = (e.nome_referencia || '').trim();
    } else {
        item.passageiro_id = e.passageiro_id;
    }
    return item;
};

const SaidaMilhasModal = ({ visible, onClose, onSuccess, editingTransaction = null, editingVenda = null, isMobile = false }) => {
    const isEditMode = Boolean(editingTransaction) || Boolean(editingVenda);
    const isEditVenda = Boolean(editingVenda);
    const { temModuloFinanceiro } = useAuth();
    const [form] = Form.useForm();
    const [messageApi, contextHolder] = message.useMessage();
    const [loading, setLoading] = useState(false);
    const [tipoOperacao, setTipoOperacao] = useState('saida_avulsa');
    const [upsellFinanceiroOpen, setUpsellFinanceiroOpen] = useState(false);

    const [portadores, setPortadores] = useState([]);
    const [programas, setProgramas] = useState([]);
    const [passageiros, setPassageiros] = useState([]);
    const [contas, setContas] = useState([]);
    const [categoriasReceita, setCategoriasReceita] = useState([]);
    
    const [loadingPortadores, setLoadingPortadores] = useState(false);
    const [loadingProgramas, setLoadingProgramas] = useState(false);
    
    const [selectedProgramaData, setSelectedProgramaData] = useState(null);
    const [isPassageiroModalVisible, setIsPassageiroModalVisible] = useState(false);

    const criarContaReceberWatch = Form.useWatch('criar_conta_receber', form);
    const dataVendaWatch = Form.useWatch('data_venda', form);
    const valorVendaWatch = Form.useWatch('valor_total_venda', form);

    const currencyFormatter = (value) => {
        if (value === null || value === undefined || value === '') return '';
        return new Intl.NumberFormat('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    };

    const currencyParser = (value) => {
        if (!value) return null;
        const onlyNumbers = value.replace(/[^\d]/g, '');
        if (!onlyNumbers) return null;
        const num = parseInt(onlyNumbers, 10);
        return num / 100;
    };

    const fetchPassageiros = useCallback(async () => {
        try {
            const res = await getPassageiros();
            setPassageiros(res.data);
        } catch (error) {
            messageApi.error("Falha ao carregar a lista de passageiros.");
        }
    }, [messageApi]);

    const fetchFinanceiroOptions = useCallback(async () => {
        if (!temModuloFinanceiro) return;
        try {
            const [contasData, categoriasData] = await Promise.all([
                getContas('ATIVO'),
                getCategorias(),
            ]);
            const contasList = Array.isArray(contasData) ? contasData : (contasData?.results || []);
            const categoriasList = Array.isArray(categoriasData) ? categoriasData : (categoriasData?.results || []);
            setContas(contasList);
            setCategoriasReceita(categoriasList.filter((c) => c.tipo === 'receita'));
        } catch (error) {
            messageApi.error(getErrorMessage(error, 'Falha ao carregar contas/categorias.'));
        }
    }, [temModuloFinanceiro, messageApi]);

    const fetchInitialData = useCallback(async () => {
        setLoadingPortadores(true);
        try {
            const portadoresRes = await getPortadores();
            const lista = portadoresRes.data || [];
            setPortadores(lista);
            // Com um único portador não há o que escolher: já deixa preenchido.
            if (lista.length === 1) {
                form.setFieldsValue({ portador: lista[0].id });
                await handlePortadorChange(lista[0].id);
            }
        } catch (error) {
            messageApi.error(getErrorMessage(error, 'Falha ao carregar portadores.'));
        } finally {
            setLoadingPortadores(false);
        }
    }, [form, messageApi]);

    const loadEditData = useCallback(async () => {
        const source = editingVenda || editingTransaction;
        if (!source) return;
        setLoadingPortadores(true);
        setLoadingProgramas(true);
        try {
            const portadorId = source.portador_id;
            const [portadoresRes, programasRes, passageirosRes] = await Promise.all([
                getPortadores(),
                getProgramasDoPortador(portadorId),
                getPassageiros(),
            ]);
            const portadoresData = portadoresRes.data;
            const programasData = programasRes.data;
            const passageirosData = passageirosRes.data;
            setPortadores(portadoresData);
            setProgramas(programasData);
            setPassageiros(passageirosData);

            const ppId = typeof source.programa_portador === 'object'
                ? source.programa_portador?.id : source.programa_portador;
            const programaSelecionado = programasData.find(p => p.id === ppId);
            setSelectedProgramaData(programaSelecionado || null);

            const emissoesForm = (source.emissoes || []).map(e => ({
                id: e.id,
                passageiro_id: typeof e.passageiro === 'object' ? e.passageiro?.id : e.passageiro,
                cpf_desconhecido: Boolean(e.cpf_desconhecido),
                nome_referencia: e.nome_referencia || e.passageiro_nome || '',
                quantidade_milhas_utilizada: e.quantidade_milhas_utilizada,
                data_emissao: e.data_emissao ? dayjs(e.data_emissao) : null,
                codigo_localizador: e.codigo_localizador || '',
            }));

            if (editingVenda) {
                setTipoOperacao('venda');
                form.setFieldsValue({
                    tipo_operacao: 'venda',
                    portador: portadorId,
                    programa_portador: ppId,
                    quantidade_milhas_vendidas: parseFloat(editingVenda.quantidade_milhas_vendidas) || 0,
                    valor_total_venda: parseFloat(editingVenda.valor_total_venda) || 0,
                    custo_operacao_adicional: parseFloat(editingVenda.custo_operacao_adicional) || 0,
                    data_venda: editingVenda.data_venda ? dayjs(editingVenda.data_venda) : null,
                    observacao: editingVenda.observacao ?? '',
                    emissoes_iniciais: emissoesForm,
                });
            } else {
                setTipoOperacao('saida_avulsa');
                form.setFieldsValue({
                    tipo_operacao: 'saida_avulsa',
                    portador: portadorId,
                    programa_portador: ppId,
                    quantidade_total_milhas: parseFloat(editingTransaction.quantidade_milhas) || 0,
                    data_operacao: editingTransaction.data_transacao ? dayjs(editingTransaction.data_transacao) : null,
                    observacao: editingTransaction.observacao ?? '',
                    emissoes: emissoesForm,
                });
            }
        } catch (err) {
            messageApi.error(getErrorMessage(err, 'Falha ao carregar dados para edição.'));
        } finally {
            setLoadingPortadores(false);
            setLoadingProgramas(false);
        }
    }, [editingTransaction, editingVenda, form, messageApi]);

    useEffect(() => {
        if (visible) {
            if (editingTransaction || editingVenda) {
                loadEditData();
            } else {
                form.resetFields();
                setTipoOperacao('saida_avulsa');
                setSelectedProgramaData(null);
                form.setFieldsValue({
                    tipo_operacao: 'saida_avulsa',
                    emissoes: [],
                    emissoes_iniciais: [],
                    criar_conta_receber: false,
                });
                fetchInitialData();
                fetchPassageiros();
                fetchFinanceiroOptions();
            }
        }
    }, [visible, editingTransaction, editingVenda, form, fetchInitialData, fetchPassageiros, fetchFinanceiroOptions, loadEditData]);

    useEffect(() => {
        if (!criarContaReceberWatch || tipoOperacao !== 'venda') return;
        const patch = {};
        if (valorVendaWatch != null && form.getFieldValue('cr_valor') == null) {
            patch.cr_valor = valorVendaWatch;
        }
        if (dataVendaWatch && !form.getFieldValue('cr_data_vencimento')) {
            patch.cr_data_vencimento = dataVendaWatch;
        }
        if (!form.getFieldValue('cr_descricao') && selectedProgramaData) {
            patch.cr_descricao = `Venda de milhas – ${selectedProgramaData.programa_nome || 'programa'}`;
        }
        if (Object.keys(patch).length) form.setFieldsValue(patch);
    }, [criarContaReceberWatch, tipoOperacao, valorVendaWatch, dataVendaWatch, selectedProgramaData, form]);

    const handlePortadorChange = async (portadorId) => {
        form.setFieldsValue({ programa_portador: null });
        setSelectedProgramaData(null);
        setLoadingProgramas(true);
        try {
            const res = await getProgramasDoPortador(portadorId);
            setProgramas(res.data);
        } catch (error) {
            messageApi.error(getErrorMessage(error, 'Falha ao carregar programas.'));
        } finally {
            setLoadingProgramas(false);
        }
    };

    const handleProgramaChange = (programaId) => {
        const programaSelecionado = programas.find(p => p.id === programaId);
        setSelectedProgramaData(programaSelecionado);
    };

    const handlePassageiroCriado = (novoPassageiro) => {
        setIsPassageiroModalVisible(false);
        setPassageiros(prev => [...prev, novoPassageiro]);
    };

    const handleFormSubmit = async (closeOnSuccess = true) => {
        try {
            const values = await form.validateFields();
            setLoading(true);
            let successMessage = '';

            let apiResponse = null;
            if (isEditVenda && editingVenda) {
                apiResponse = await updateOperacaoVenda(editingVenda.id, {
                    quantidade_milhas_vendidas: values.quantidade_milhas_vendidas,
                    valor_total_venda: values.valor_total_venda,
                    custo_operacao_adicional: values.custo_operacao_adicional || 0,
                    data_venda: values.data_venda.format('YYYY-MM-DD'),
                    observacao: values.observacao ?? '',
                    emissoes: (values.emissoes_iniciais || []).map(mapEmissaoPayload),
                });
                successMessage = 'Operação de Venda atualizada com sucesso!';
            } else if (isEditMode && editingTransaction) {
                apiResponse = await updateTransacaoMilhas(editingTransaction.id, {
                    data_transacao: values.data_operacao.format('YYYY-MM-DD'),
                    observacao: values.observacao ?? '',
                    emissoes: (values.emissoes || []).map(mapEmissaoPayload),
                });
                successMessage = 'Saída Avulsa atualizada com sucesso!';
            } else if (tipoOperacao === 'saida_avulsa') {
                const payload = {
                    programa_portador: values.programa_portador,
                    quantidade_total_milhas: values.quantidade_total_milhas,
                    data_operacao: values.data_operacao.format('YYYY-MM-DD'),
                    observacao: values.observacao,
                    emissoes: (values.emissoes || []).map(mapEmissaoPayload),
                };
                apiResponse = await registrarSaidaAvulsa(payload);
                successMessage = 'Saída Avulsa registrada com sucesso!';
            } else {
                const emissoesIniciais = (values.emissoes_iniciais || []).map(mapEmissaoPayload);
                const payload = {
                    programa_portador: values.programa_portador,
                    quantidade_milhas_vendidas: values.quantidade_milhas_vendidas,
                    valor_total_venda: values.valor_total_venda,
                    custo_operacao_adicional: values.custo_operacao_adicional || 0,
                    data_venda: values.data_venda.format('YYYY-MM-DD'),
                    observacao: values.observacao,
                    emissoes_iniciais: emissoesIniciais,
                };
                apiResponse = await registrarVenda(payload);
                successMessage = emissoesIniciais.length === 0
                    ? 'Venda registrada em aberto. Adicione emissões para debitar o saldo no extrato.'
                    : 'Operação de Venda registrada com sucesso!';

                if (temModuloFinanceiro && values.criar_conta_receber) {
                    try {
                        await createTransacao({
                            tipo: 'receita',
                            descricao: values.cr_descricao,
                            valor: values.cr_valor,
                            data_vencimento: values.cr_data_vencimento.format('YYYY-MM-DD'),
                            status: 'PENDENTE',
                            conta: values.cr_conta,
                            categoria: values.cr_categoria,
                            eh_cobranca: true,
                            nome_devedor: values.cr_nome_devedor || null,
                        });
                        successMessage += ' Conta a receber criada.';
                    } catch (crError) {
                        messageApi.warning(
                            getErrorMessage(crError, 'Venda salva, mas falhou ao criar a conta a receber.'),
                            8,
                        );
                    }
                }
            }

            if (apiResponse?.aviso_cpf?.aviso && apiResponse.aviso_cpf.mensagem) {
                messageApi.warning(apiResponse.aviso_cpf.mensagem, 8);
            }
            onSuccess(closeOnSuccess, successMessage);

            if (!closeOnSuccess) {
                const currentPortador = values.portador;
                const currentPrograma = values.programa_portador;
                form.resetFields();
                form.setFieldsValue({
                    tipo_operacao: tipoOperacao,
                    portador: currentPortador,
                    programa_portador: currentPrograma,
                    emissoes: [],
                    emissoes_iniciais: [],
                    criar_conta_receber: false,
                });
            }
        } catch (error) {
            if (error?.errorFields) return;
            messageApi.error(getErrorMessage(error, 'Erro ao salvar. Verifique os campos.'));
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = () => handleFormSubmit(true);
    const handleSubmitAndAddNew = () => handleFormSubmit(false);

    const renderEmissaoRow = (fieldListName, name, restField, remove) => (
        <div key={name} style={{ marginBottom: 12, padding: 12, border: '1px solid #f0f0f0', borderRadius: 8 }}>
            <Form.Item {...restField} name={[name, 'id']} hidden><Input type="hidden" /></Form.Item>
            <Form.Item {...restField} name={[name, 'cpf_desconhecido']} valuePropName="checked" initialValue={false} style={{ marginBottom: 8 }}>
                <Checkbox>Não tenho o CPF (reserva 1 vaga na contagem)</Checkbox>
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(prev, curr) => {
                const prevList = prev[fieldListName] || [];
                const currList = curr[fieldListName] || [];
                return prevList[name]?.cpf_desconhecido !== currList[name]?.cpf_desconhecido;
            }}>
                {() => {
                    const desconhecido = form.getFieldValue([fieldListName, name, 'cpf_desconhecido']);
                    return desconhecido ? (
                        <Form.Item
                            {...restField}
                            name={[name, 'nome_referencia']}
                            label="Nome ou referência"
                            rules={[{ required: true, message: 'Informe o nome ou uma referência' }]}
                        >
                            <Input placeholder="Ex.: emissão para empresa / passageiro do dia" />
                        </Form.Item>
                    ) : (
                        <Form.Item
                            {...restField}
                            name={[name, 'passageiro_id']}
                            label="Passageiro"
                            rules={[{ required: true, message: 'Obrigatório' }]}
                        >
                            <Select
                                showSearch
                                placeholder="Passageiro"
                                filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
                                dropdownRender={(menu) => (
                                    <>
                                        {menu}
                                        <Divider style={{ margin: '8px 0' }} />
                                        <Button type="link" icon={<PlusOutlined />} onClick={() => setIsPassageiroModalVisible(true)}>
                                            Adicionar Novo Passageiro
                                        </Button>
                                    </>
                                )}
                            >
                                {passageiros.map(p => <Option key={p.id} value={p.id}>{p.nome_completo}</Option>)}
                            </Select>
                        </Form.Item>
                    );
                }}
            </Form.Item>
            <Space wrap style={{ width: '100%' }} align="start">
                <Form.Item {...restField} name={[name, 'quantidade_milhas_utilizada']} rules={[{ required: true, message: 'Obrigatório' }]}>
                    <InputNumber placeholder="Milhas" style={{ width: 120 }} formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')} parser={value => value.replace(/\./g, '')} />
                </Form.Item>
                <Form.Item {...restField} name={[name, 'data_emissao']} rules={[{ required: true, message: 'Obrigatório' }]}>
                    <DatePicker format="DD/MM/YYYY" />
                </Form.Item>
                <Form.Item {...restField} name={[name, 'codigo_localizador']}>
                    <Input placeholder="Localizador" />
                </Form.Item>
                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} />
            </Space>
        </div>
    );

    const renderEmissoesFields = (fieldListName) => (
        <Form.List name={fieldListName}>
            {(fields, { add, remove }) => (
                <>
                    {fields.map(({ key, name, ...restField }) => (
                        <React.Fragment key={key}>
                            {renderEmissaoRow(fieldListName, name, restField, remove)}
                        </React.Fragment>
                    ))}
                    <Form.Item>
                        <Button type="dashed" onClick={() => add({ cpf_desconhecido: false })} block icon={<PlusOutlined />}>
                            Adicionar Emissão
                        </Button>
                    </Form.Item>
                </>
            )}
        </Form.List>
    );

    const renderContaReceberFields = () => {
        if (tipoOperacao !== 'venda' || isEditMode) return null;
        return (
            <>
                <Divider>Conta a Receber (opcional)</Divider>
                <Form.Item name="criar_conta_receber" valuePropName="checked" initialValue={false}>
                    <Checkbox
                        onChange={(e) => {
                            if (e.target.checked && !temModuloFinanceiro) {
                                form.setFieldsValue({ criar_conta_receber: false });
                                setUpsellFinanceiroOpen(true);
                            }
                        }}
                    >
                        Criar conta a receber com o valor desta venda
                    </Checkbox>
                </Form.Item>
                {criarContaReceberWatch && temModuloFinanceiro && (
                    <>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                            Disponível porque você tem o módulo financeiro. O lançamento fica pendente (inclusive com data futura).
                        </Text>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="cr_conta" label="Conta" rules={[{ required: true, message: 'Selecione a conta' }]}>
                                    <Select placeholder="Conta" options={contas.map((c) => ({ value: c.id, label: c.nome }))} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="cr_categoria" label="Categoria" rules={[{ required: true, message: 'Selecione a categoria' }]}>
                                    <Select placeholder="Categoria de receita" options={categoriasReceita.map((c) => ({ value: c.id, label: c.nome }))} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item name="cr_descricao" label="Descrição" rules={[{ required: true, message: 'Informe a descrição' }]}>
                            <Input placeholder="Venda de milhas" />
                        </Form.Item>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item name="cr_valor" label="Valor" rules={[{ required: true, message: 'Informe o valor' }]}>
                                    <InputNumber prefix="R$ " style={{ width: '100%' }} min={0} formatter={currencyFormatter} parser={currencyParser} />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="cr_data_vencimento" label="Vencimento" rules={[{ required: true, message: 'Informe o vencimento' }]}>
                                    <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="cr_nome_devedor" label="Devedor (opcional)">
                                    <Input placeholder="Nome do cliente/empresa" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </>
                )}
            </>
        );
    };

    const renderSaidaAvulsaFields = () => (
        <>
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item name="quantidade_total_milhas" label="Quantidade Total de Milhas" rules={[{ required: true, message: "Campo obrigatório" }]}>
                        <InputNumber style={{ width: '100%' }} min={1} formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')} parser={value => value.replace(/\./g, '')}/>
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item name="data_operacao" label="Data da Operação" rules={[{ required: true, message: "Campo obrigatório" }]}>
                        <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
            </Row>
            <Divider>Vincular Emissões (Opcional)</Divider>
            {renderEmissoesFields('emissoes')}
        </>
    );

    const renderVendaFields = () => (
        <>
            <Row gutter={16}>
                <Col span={8}>
                    <Form.Item name="quantidade_milhas_vendidas" label="Milhas Vendidas" rules={[{ required: true }]}>
                        <InputNumber style={{ width: '100%' }} min={1} formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')} parser={value => value.replace(/\./g, '')} />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item name="valor_total_venda" label="Valor Total da Venda" rules={[{ required: true }]}>
                        <InputNumber prefix="R$ " style={{ width: '100%' }} min={0} formatter={currencyFormatter} parser={currencyParser} />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item name="data_venda" label="Data da Venda" rules={[{ required: true }]}>
                        <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
            </Row>
            <Divider>{isEditVenda ? 'Emissões da Venda' : 'Emissões Iniciais (Opcional)'}</Divider>
            {renderEmissoesFields('emissoes_iniciais')}
            {!isEditVenda && renderContaReceberFields()}
        </>
    );

    const saldoBlock = selectedProgramaData && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={12}>
                <div style={{ padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: '6px', backgroundColor: 'var(--color-surface-muted, #F8F4EC)' }}>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5A6A6A)' }}>Saldo Atual</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{parseFloat(selectedProgramaData.saldo_atual).toLocaleString('pt-BR')} pts</div>
                </div>
            </Col>
            <Col span={12}>
                <div style={{ padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: '6px', backgroundColor: 'var(--color-surface-muted, #F8F4EC)' }}>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5A6A6A)' }}>Custo Médio do Milheiro (CM)</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                        R$ {parseFloat(selectedProgramaData.custo_medio_milheiro).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                </div>
            </Col>
        </Row>
    );

    const sheetTitle = isEditVenda
        ? 'Editar Saída de Venda'
        : isEditMode
            ? 'Editar Saída Avulsa'
            : 'Saída de Milhas';

    const formContent = (
                <Form form={form} layout="vertical" initialValues={{ tipo_operacao: 'saida_avulsa' }} size={isMobile ? 'large' : 'middle'} scrollToFirstError>
                    {isEditMode ? (
                        <>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="portador" label="Portador">
                                        <Select placeholder="Portador" disabled loading={loadingPortadores}>
                                            {portadores.map(p => <Option key={p.id} value={p.id}>{p.nome_completo}</Option>)}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="programa_portador" label="Programa">
                                        <Select placeholder="Programa" disabled loading={loadingProgramas}>
                                            {programas.map(p => <Option key={p.id} value={p.id}>{p.programa_nome}</Option>)}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                            {saldoBlock}
                            {isEditVenda ? (
                                <>
                                    {renderVendaFields()}
                                    <Form.Item name="observacao" label="Observação"><Input.TextArea rows={2} /></Form.Item>
                                </>
                            ) : (
                                <>
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item name="quantidade_total_milhas" label="Quantidade Total de Milhas" rules={[{ required: true, message: "Campo obrigatório" }]}>
                                                <InputNumber style={{ width: '100%' }} min={1} formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')} parser={value => value.replace(/\./g, '')} disabled />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item name="data_operacao" label="Data da Operação" rules={[{ required: true, message: "Campo obrigatório" }]}>
                                                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Divider>Emissões Vinculadas</Divider>
                                    {renderEmissoesFields('emissoes')}
                                    <Form.Item name="observacao" label="Observação"><Input.TextArea rows={2} /></Form.Item>
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            <Row gutter={16}>
                                <Col span={12}><Form.Item name="portador" label="Portador" rules={[{ required: true }]}><Select placeholder="Selecione o portador" onChange={handlePortadorChange} loading={loadingPortadores}>{portadores.map(p => <Option key={p.id} value={p.id}>{p.nome_completo}</Option>)}</Select></Form.Item></Col>
                                <Col span={12}><Form.Item label="Programa" name="programa_portador" rules={[{ required: true }]}><Select placeholder="Selecione o programa" loading={loadingProgramas} onChange={handleProgramaChange}>{programas.map(p => <Option key={p.id} value={p.id}>{p.programa_nome}</Option>)}</Select></Form.Item></Col>
                            </Row>
                            {saldoBlock}
                            <Form.Item name="tipo_operacao">
                                <Radio.Group onChange={(e) => setTipoOperacao(e.target.value)}>
                                    <Radio.Button value="saida_avulsa">Saída Avulsa</Radio.Button>
                                    <Radio.Button value="venda">Saída de Venda</Radio.Button>
                                </Radio.Group>
                            </Form.Item>
                            <Divider />
                            {tipoOperacao === 'saida_avulsa' ? renderSaidaAvulsaFields() : renderVendaFields()}
                            <Form.Item name="observacao" label="Observação"><Input.TextArea rows={2} /></Form.Item>
                        </>
                    )}
                </Form>
    );

    const nested = (
        <>
            <PassageiroModal 
                visible={isPassageiroModalVisible}
                onClose={() => setIsPassageiroModalVisible(false)}
                onSuccess={handlePassageiroCriado}
            />
            <ModuloUpsellModal
                open={upsellFinanceiroOpen}
                onClose={() => setUpsellFinanceiroOpen(false)}
                moduloFaltante="financeiro"
            />
        </>
    );

    if (isMobile) {
        return (
            <>
                {contextHolder}
                <MobileLaunchSheet
                    open={visible}
                    title={sheetTitle}
                    onCancel={onClose}
                    onSave={handleSubmit}
                    saveLoading={loading}
                    secondaryAction={
                        !isEditMode ? (
                            <Button type="default" loading={loading} onClick={handleSubmitAndAddNew} block size="large">
                                Salvar e Adicionar Outro
                            </Button>
                        ) : null
                    }
                >
                    {formContent}
                </MobileLaunchSheet>
                {nested}
            </>
        );
    }

    return (
        <>
            {contextHolder}
            <Modal 
                title={
                    isEditVenda
                        ? 'Editar Saída de Venda'
                        : isEditMode
                            ? 'Editar Saída Avulsa'
                            : 'Lançar Nova Saída de Milhas'
                } 
                open={visible} 
                onCancel={onClose} 
                width={800}
                destroyOnClose
                footer={isEditMode ? [
                    <Button key="back" onClick={onClose}>Cancelar</Button>,
                    <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>Salvar</Button>
                ] : [
                    <Button key="back" onClick={onClose}>Cancelar</Button>,
                    <Button key="add" type="default" loading={loading} onClick={handleSubmitAndAddNew}>Salvar e Adicionar Outro</Button>,
                    <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>Salvar</Button>
                ]}
            >
                {formContent}
            </Modal>
            {nested}
        </>
    );
};

export default SaidaMilhasModal;
